import { useState } from 'react';
import { api } from '../api';
import { useLoad, usePolling } from '../hooks';
import { compactNumber, duration, money, stamp, timeAgo } from '../format';
import {
  Card,
  ConfirmButton,
  Empty,
  ErrorNote,
  Field,
  Loading,
  Metric,
  Modal,
  StatusPill,
  Table,
  Toggle,
} from '../components/ui';
import { useNavigate } from '../router';
import type {
  ActivityEvent,
  Diagnostics,
  ActorSummary,
  DailySpendPoint,
  OperationsError,
  OperationsOverview,
  Page,
  Schedule,
} from '../types';

/**
 * İzleme.
 *
 * Dört soruyu cevaplıyor: ne çalıştı, ne bozuldu, ne kadar tuttu, ve bundan sonra ne
 * kendiliğinden çalışacak. Panel bunların hepsini tek yerde göstermezse operatör
 * harcamayı ancak fatura gelince, hatayı ancak veri eksik çıkınca fark eder.
 *
 * Hiçbir şey Instagram'a özel değil: "tarama" burada çalışma, "görsel analizi" ise iş
 * kaydı. Yarın başka bir actor eklendiğinde bu sayfa onu da gösterir.
 */

const WINDOWS = [
  { hours: 1, label: 'Son 1 saat' },
  { hours: 24, label: 'Son 24 saat' },
  { hours: 24 * 7, label: 'Son 7 gün' },
  { hours: 24 * 30, label: 'Son 30 gün' },
];

const SEKMELER = [
  { id: 'genel', label: 'Genel' },
  { id: 'akis', label: 'Canlı akış' },
  { id: 'hatalar', label: 'Hatalar' },
  { id: 'sistem', label: 'Sistem' },
  { id: 'zamanlama', label: 'Zamanlamalar' },
] as const;
type Sekme = (typeof SEKMELER)[number]['id'];

export function Operations() {
  const [hours, setHours] = useState(24);
  const [sekme, setSekme] = useState<Sekme>('genel');

  const overview = useLoad(
    () => api.get<OperationsOverview>(`/v1/operations/overview?hours=${hours}`),
    [hours],
  );
  const errors = useLoad(
    () => api.get<{ items: OperationsError[] }>(`/v1/operations/errors?hours=${hours}&limit=200`),
    [hours],
  );
  const daily = useLoad(() =>
    api.get<{ items: DailySpendPoint[] }>('/v1/operations/spend/daily?days=14'),
  );
  const schedules = useLoad(() => api.get<{ items: Schedule[] }>('/v1/schedules'));
  const diag = useLoad(() => api.get<Diagnostics>('/v1/operations/diagnostics'));

  usePolling(overview.reload, 20_000);
  usePolling(errors.reload, 20_000);
  usePolling(schedules.reload, 30_000);
  usePolling(diag.reload, 10_000);

  const errorItems = errors.data?.items ?? [];

  return (
    <>
      {/* Tek bakışta cevap. Sekiz kartı alt alta dizmek "iyi mi kötü mü" sorusunu
          cevapsız bırakıyordu: hepsi eşit ağırlıktaydı, hiçbiri öne çıkmıyordu. Bu şerit
          her sekmede duruyor, çünkü soru her sekmede aynı. */}
      <HealthBar diag={diag.data} overview={overview.data} errorCount={errorItems.length} />

      <div className="tabs">
        {SEKMELER.map((t) => (
          <button
            key={t.id}
            type="button"
            className={t.id === sekme ? 'tab on' : 'tab'}
            onClick={() => setSekme(t.id)}
          >
            {t.label}
            {t.id === 'hatalar' && errorItems.length > 0 ? (
              <span className="tab-count">{errorItems.length}</span>
            ) : null}
          </button>
        ))}
        <span className="tabs-spacer" />
        {/* Pencere yalnız GEÇMİŞE bakan sekmeleri ilgilendiriyor; canlı akış ve sistem
            "şu an" anlatıyor ve seçimden etkilenmiyor. */}
        {sekme === 'genel' || sekme === 'hatalar' ? (
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {WINDOWS.map((w) => (
              <option key={w.hours} value={w.hours}>
                {w.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {sekme === 'genel' && (
        <>
          <SpendCard overview={overview.data} loading={overview.loading} error={overview.error} />
          <ScrapingCard overview={overview.data} />
          <DailyCard points={daily.data?.items ?? []} loading={daily.loading} error={daily.error} />
        </>
      )}

      {sekme === 'akis' && <ActivityCard />}

      {sekme === 'hatalar' && (
        <ErrorsCard items={errorItems} loading={errors.loading} error={errors.error} />
      )}

      {sekme === 'sistem' && (
        <SystemCard diag={diag.data} loading={diag.loading} error={diag.error} />
      )}

      {sekme === 'zamanlama' && (
        <SchedulesCard
          items={schedules.data?.items ?? []}
          loading={schedules.loading}
          error={schedules.error}
          reload={schedules.reload}
        />
      )}
    </>
  );
}

/* -- sağlık şeridi -------------------------------------------------------- */

/**
 * "Şu an her şey yolunda mı?" — tek satır.
 *
 * Sıralama bilinçli: önce en sessiz arıza (işleyici durmuş), sonra biriken iş, sonra
 * hata, en sonda para. Çünkü duran bir işleyici hiçbir sayaçta görünmüyor; kuyruk sessizce
 * büyüyor ve harcama düştüğü için grafik "her şey sakin" gibi okunuyor.
 */
function HealthBar({
  diag,
  overview,
  errorCount,
}: {
  diag: Diagnostics | null;
  overview: OperationsOverview | null;
  errorCount: number;
}) {
  const w = diag?.ocrWorker;
  const kuyruk = diag?.queues.ocrJobs.QUEUED ?? 0;
  const kotaBitmis = (w?.consecutiveRateLimits ?? 0) >= 3;
  const isleyiciDurdu = diag !== null && w?.alive === false;

  const durum: { tone: 'ok' | 'warn' | 'bad'; text: string } = isleyiciDurdu
    ? { tone: 'bad', text: 'Görsel işleyici yanıt vermiyor' }
    : kotaBitmis
      ? { tone: 'bad', text: 'Model kotası tükenmiş' }
      : errorCount > 0
        ? { tone: 'warn', text: `${errorCount} hata var` }
        : diag === null
          ? { tone: 'warn', text: 'Durum okunamadı' }
          : { tone: 'ok', text: 'Sistem sağlıklı' };

  return (
    <div className={`ops-health oh-${durum.tone}`}>
      <span className="oh-verdict">
        <span className="pip" /> {durum.text}
      </span>

      <span className="oh-item">
        <b>
          {w?.alive
            ? w.rateLimitedForSec > 0
              ? `${w.rateLimitedForSec} sn bekliyor`
              : 'çalışıyor'
            : '—'}
        </b>
        işleyici
      </span>
      <span className="oh-item">
        <b>{compactNumber(kuyruk)}</b> sırada
      </span>
      <span className="oh-item">
        <b>{compactNumber(diag?.queues.runs.RUNNING ?? 0)}</b> tarama sürüyor
      </span>
      <span className="oh-item">
        <b>{overview ? money(overview.imageAnalysis.costMicroUsd) : '—'}</b> harcama
      </span>
      <span className="oh-item oh-when">{diag ? timeAgo(diag.at) : ''}</span>
    </div>
  );
}

/* -- sistem durumu -------------------------------------------------------- */

/**
 * Şu anki sistem durumu.
 *
 * En üstte duruyor çünkü diğer bütün kartlar GEÇMİŞİ anlatıyor ve bir şey durduğunda
 * geçmiş sessizleşir — "son bir saatte 0 çağrı" hem "iş yoktu" hem "işleyici öldü"
 * demek olabilir. Bu kart ikisini ayırır.
 *
 * On saniyede bir yenileniyor ve pencere seçiminden BAĞIMSIZ: buradaki hiçbir sayı bir
 * zaman aralığına ait değil, hepsi "şu an".
 */
function SystemCard({
  diag: d,
  loading,
  error,
}: {
  diag: Diagnostics | null;
  loading: boolean;
  error: string | null;
}) {
  return (
    <Card title="Sistem" flush>
      <div style={{ padding: '14px 16px 0' }}>
        <ErrorNote message={error} />
      </div>
      {!d && loading && <Loading />}
      {d && (
        <>
          {/* Görsel işleyici en başta: sessizce duran ve kuyruğu büyüten parça buydu.
              Üç ayrı durum var ve ikisi dışarıdan aynı görünüyordu — bu yüzden üçü de
              ayrı ayrı yazılıyor. */}
          <div className="sys-banner">
            {!d.ocrWorker.alive ? (
              <span className="badge b-danger">
                Görsel işleyici YANIT VERMİYOR
                {d.ocrWorker.secondsSinceSeen !== null
                  ? ` — ${duration(d.ocrWorker.secondsSinceSeen * 1000)} sessiz`
                  : ' — hiç kalp atışı yok'}
              </span>
            ) : (d.ocrWorker.consecutiveRateLimits ?? 0) >= 3 ? (
              /* Üst üste sınır, beklemekle geçen bir yavaşlama değil: kota tükenmiş
                 demektir ve operatörün yapması gereken beklemek değil, kotayı açmaktır. */
              <span className="badge b-danger">
                Model kotası tükenmiş görünüyor — üst üste {d.ocrWorker.consecutiveRateLimits} hız
                sınırı, hiçbir görsel işlenmiyor
              </span>
            ) : d.ocrWorker.rateLimitedForSec > 0 ? (
              <span className="badge b-warn">
                Görsel işleyici hız sınırında bekliyor — {d.ocrWorker.rateLimitedForSec} sn kaldı
              </span>
            ) : (
              <span className="badge b-ok">
                Görsel işleyici çalışıyor
                {d.ocrWorker.concurrency ? ` · ${d.ocrWorker.concurrency} eşzamanlı` : ''}
              </span>
            )}
          </div>

          <div className="metrics">
            <Metric
              label="Görsel kuyruğu"
              value={compactNumber(d.queues.ocrJobs.QUEUED ?? 0)}
              hint={`${d.queues.ocrJobs.RUNNING ?? 0} işleniyor · ${d.queues.ocrJobs.FAILED ?? 0} başarısız`}
              tone={(d.queues.ocrJobs.QUEUED ?? 0) > 200 ? 'bad' : undefined}
            />
            <Metric
              label="Tarama kuyruğu"
              value={compactNumber((d.queues.runs.QUEUED ?? 0) + (d.queues.runs.RUNNING ?? 0))}
              hint={`${d.queues.runs.RUNNING ?? 0} çalışıyor · ${d.queues.runs.FAILED ?? 0} başarısız`}
            />
            <Metric
              label="Veritabanı"
              value={`${d.database.pingMs} ms`}
              /* Ping tek başına yanıltıcı: havuz dolduğunda sorgular hızlı kalır ama
                 sıra uzar. İkisi yan yana olmazsa "veritabanı yavaş" yanlış teşhisi çıkar. */
              hint={`${d.database.connectionsTotal}/${d.database.connectionsMax ?? '?'} bağlantı`}
              tone={
                d.database.connectionsMax &&
                d.database.connectionsTotal > d.database.connectionsMax * 0.8
                  ? 'bad'
                  : undefined
              }
            />
            <Metric
              label="Olay döngüsü"
              value={`${d.process.eventLoopDelayMs.p99} ms`}
              /* p99 yüksekse istekler CPU bekliyordur — veritabanı hızlı olsa bile. */
              hint={`p50 ${d.process.eventLoopDelayMs.p50} ms · tepe ${d.process.eventLoopDelayMs.max} ms`}
              tone={d.process.eventLoopDelayMs.p99 > 100 ? 'bad' : undefined}
            />
            <Metric
              label="Makine yükü"
              value={d.host.loadPerCpu.m1.toFixed(2)}
              hint={`çekirdek başına · ${d.host.cpuCount} çekirdek · 5dk ${d.host.loadPerCpu.m5.toFixed(2)}`}
              tone={d.host.loadPerCpu.m1 > 1 ? 'bad' : undefined}
            />
            <Metric
              label="Boş bellek"
              value={`${Math.round(d.host.memFreeMb / 1024)} GB`}
              hint={`${Math.round(d.host.memTotalMb / 1024)} GB toplam · API ${d.process.rssMb} MB`}
              tone={d.host.memFreeMb < d.host.memTotalMb * 0.1 ? 'bad' : undefined}
            />
          </div>

          {/* Kuyruk durumlarının tam dökümü: yukarıdaki metrikler öne çıkanları gösteriyor,
              burada hiçbir durum gizlenmiyor. */}
          <div className="sys-breakdown">
            <span className="sys-group">
              <b>Taramalar</b>
              {Object.entries(d.queues.runs).map(([k, v]) => (
                <span key={k} className="sys-chip">
                  {k}: {v}
                </span>
              ))}
            </span>
            <span className="sys-group">
              <b>Görseller</b>
              {Object.entries(d.queues.ocrJobs).map(([k, v]) => (
                <span key={k} className="sys-chip">
                  {k}: {v}
                </span>
              ))}
            </span>
          </div>

          <DiagErrors diag={d} />
        </>
      )}
    </Card>
  );
}

/**
 * Son 6 saatin hataları, METİNLERİYLE.
 *
 * Yukarıdaki hata tablosu pencereye bağlı ve kodları gösteriyor; kod teşhis etmiyor.
 * `ACTOR_EXECUTION_FAILED` hiçbir şey anlatmıyor, altındaki cümle anlatıyor — hesabın
 * silindiğini ancak o söylüyor.
 */
function DiagErrors({ diag }: { diag: Diagnostics }) {
  const runs = diag.recentErrors.runs;
  const jobs = diag.recentErrors.ocrJobs;
  if (runs.length === 0 && jobs.length === 0) return null;

  return (
    <div className="sys-errors">
      {runs.length > 0 && (
        <>
          <h4>Tarama hataları ({runs.length})</h4>
          <Table head={['Ne zaman', 'Actor', 'Girdi', 'Kod', 'Sebep', 'Deneme']}>
            {runs.map((r) => (
              <tr key={r.id}>
                <td className="muted" title={stamp(r.at)}>
                  {timeAgo(r.at)}
                </td>
                <td className="mono">{r.actor ?? '—'}</td>
                <td className="mono muted">{inputSummary(r.input)}</td>
                {/* Actor'ın kendi kodu varsa O gösteriliyor: çekirdeğin
                    `ACTOR_EXECUTION_FAILED` sarmalayıcısı hangi arıza olduğunu
                    söylemiyor, actor'ınki söylüyor. */}
                <td className="mono">{r.actorCode ?? r.code ?? '—'}</td>
                <td className="muted">{r.message ?? '—'}</td>
                <td className="muted">{r.attempt}</td>
              </tr>
            ))}
          </Table>
        </>
      )}
      {jobs.length > 0 && (
        <>
          <h4>Görsel hataları ({jobs.length})</h4>
          <Table head={['Ne zaman', 'Motor', 'Durum', 'Kod', 'Sebep', 'Deneme']}>
            {jobs.map((j) => (
              <tr key={j.id}>
                <td className="muted" title={stamp(j.at)}>
                  {timeAgo(j.at)}
                </td>
                <td className="mono">{j.engine}</td>
                <td>
                  <StatusPill status={j.status} />
                </td>
                <td className="mono">{j.code ?? '—'}</td>
                <td className="muted">{j.message ?? '—'}</td>
                <td className="muted">{j.attempt}</td>
              </tr>
            ))}
          </Table>
        </>
      )}
    </div>
  );
}

/* -- canlı akış ----------------------------------------------------------- */

/**
 * Şu an ne olduğu.
 *
 * Sayaçlar "61 çağrı yapıldı" diyor ama hangi hesabın tarandığını, hangi görselin
 * önbellekten geldiğini, neyin patladığını söylemiyor. Bir tarama başlatıp hiçbir şey
 * olmadığında bakılacak yer burası.
 *
 * Beş saniyede bir yenileniyor: pencere seçiminden BAĞIMSIZ, çünkü bu akış "son ne oldu"
 * sorusuna cevap veriyor, "bu ayda ne oldu" sorusuna değil.
 */
/** Akışta ve hatalarda ortak filtre kutusu. */
function FilterBar({ children }: { children: React.ReactNode }) {
  return <div className="filterbar">{children}</div>;
}

const DURUMLAR = [
  { v: '', l: 'Her durum' },
  { v: 'SUCCEEDED', l: 'Başarılı' },
  { v: 'FAILED', l: 'Başarısız' },
  { v: 'RUNNING', l: 'Çalışıyor' },
  { v: 'QUEUED', l: 'Sırada' },
];

function ActivityCard() {
  const [limit, setLimit] = useState(60);
  const feed = useLoad(
    () => api.get<{ items: ActivityEvent[] }>(`/v1/operations/activity?limit=${limit}`),
    [limit],
  );
  usePolling(feed.reload, 5_000);

  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [onlyBad, setOnlyBad] = useState(false);

  const items = feed.data?.items ?? [];

  /**
   * Filtreleme İSTEMCİDE.
   *
   * Bu liste "son ne oldu" penceresi ve zaten sunucudan sınırlı sayıda satır geliyor;
   * her tuşa basışta ağa çıkmak, anında olması gereken bir şeyi bekletirdi. Daha eskisine
   * bakmak gerekirse çözüm filtre değil, pencereyi büyütmek — o yüzden satır sayısı da
   * seçilebiliyor.
   */
  const kaynaklar = [...new Set(items.map((i) => i.source).filter(Boolean))].sort();
  const arama = q.trim().toLocaleLowerCase('tr');
  const gorunen = items.filter((i) => {
    if (kind && i.kind !== kind) return false;
    if (status && i.status !== status) return false;
    if (source && i.source !== source) return false;
    if (onlyBad && !i.errorCode) return false;
    if (!arama) return true;
    const metin = [i.source, i.model, i.errorCode, i.errorMessage, inputSummary(i.input)]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('tr');
    return metin.includes(arama);
  });

  const suzuldu = gorunen.length !== items.length;

  return (
    <Card
      title={`Canlı akış${suzuldu ? ` (${gorunen.length}/${items.length})` : ''}`}
      flush
      footer="En yeni olay üstte. Beş saniyede bir yenilenir; filtreler yenilemeyi durdurmaz."
      actions={
        <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
          {[60, 120, 200].map((n) => (
            <option key={n} value={n}>
              son {n} olay
            </option>
          ))}
        </select>
      }
    >
      <FilterBar>
        <input
          className="f-search"
          placeholder="Hesap, motor, hata metni…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Tarama + görsel</option>
          <option value="run">Yalnız tarama</option>
          <option value="image">Yalnız görsel</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          {DURUMLAR.map((d) => (
            <option key={d.v} value={d.v}>
              {d.l}
            </option>
          ))}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">Her kaynak</option>
          {kaynaklar.map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <label className="f-check">
          <input type="checkbox" checked={onlyBad} onChange={(e) => setOnlyBad(e.target.checked)} />
          yalnız hatalı
        </label>
        {(kind || status || source || q || onlyBad) && (
          <button
            type="button"
            className="tiny ghost"
            onClick={() => {
              setKind('');
              setStatus('');
              setSource('');
              setQ('');
              setOnlyBad(false);
            }}
          >
            Temizle
          </button>
        )}
      </FilterBar>

      <div style={{ padding: '0 16px' }}>
        <ErrorNote message={feed.error} />
      </div>
      {feed.loading && items.length === 0 && <Loading />}
      {!feed.loading && items.length === 0 && (
        <Empty title="Henüz bir şey olmadı.">
          Bir tarama başlattığında satırlar burada belirir.
        </Empty>
      )}
      {items.length > 0 && gorunen.length === 0 && (
        <Empty title="Filtreye uyan olay yok.">
          Son {items.length} olay içinde arıyorsun; daha eskisi için üstteki sayıyı büyüt.
        </Empty>
      )}
      {gorunen.map((event) => (
        <ActivityRow key={`${event.kind}-${event.id}`} event={event} />
      ))}
    </Card>
  );
}

function ActivityRow({ event }: { event: ActivityEvent }) {
  const navigate = useNavigate();
  const isRun = event.kind === 'run';

  return (
    <div className="feed-row">
      <span className="feed-time" title={stamp(event.at)}>
        {timeAgo(event.at)}
      </span>

      <span className={`feed-kind feed-kind-${event.kind}`}>{isRun ? 'tarama' : 'görsel'}</span>

      <StatusPill status={event.status} />

      <span className="feed-what">
        <span className="mono">{event.source}</span>
        {isRun && <span className="muted"> · {inputSummary(event.input)}</span>}
        {!isRun && event.model && <span className="muted mono"> · {event.model}</span>}
      </span>

      <span className="feed-facts">
        {isRun && event.itemsFound !== null && (
          <span className="muted small">
            {event.itemsFound} öğe · {event.newItems ?? 0} yeni
            {event.ocrQueued ? ` · ${event.ocrQueued} görsel` : ''}
          </span>
        )}
        {!isRun && event.cacheHit && <span className="pill pill-succeeded">önbellek</span>}
        {!isRun && !event.cacheHit && event.costMicroUsd !== null && (
          <span className="muted small">{money(event.costMicroUsd)}</span>
        )}
        {event.durationMs !== null && (
          <span className="muted small">{duration(event.durationMs)}</span>
        )}
        {event.errorCode && (
          <span className="error mono small" title={event.errorMessage ?? undefined}>
            {event.errorCode}
          </span>
        )}
        {event.runId && (
          <button
            type="button"
            className="linky small"
            onClick={() => navigate(`/runs/${event.runId}`)}
          >
            aç
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * Taramanın girdisinden okunabilir bir özet.
 *
 * Alan ADI okumuyor: "username" ya da "url" gibi isimler actor'ın şemasına ait ve panelin
 * onları bilmesi, çekirdeğe öğretmekle aynı hatayı UI'da tekrarlamak olurdu. İlk metin
 * değer alınıyor — hangi actor olursa olsun çalışır ve yanlış olduğunda da zararsızdır.
 */
function inputSummary(input: unknown): string {
  if (!input || typeof input !== 'object') return '';
  for (const value of Object.values(input as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim()) return value;
  }
  return '';
}

/* -- harcama ------------------------------------------------------------- */

function SpendCard({
  overview,
  loading,
  error,
}: {
  overview: OperationsOverview | null;
  loading: boolean;
  error: string | null;
}) {
  const ai = overview?.imageAnalysis;

  return (
    <Card
      title="Görsel analizi ve harcama"
      flush
      footer={
        overview
          ? `Fiyatlar ${overview.pricesUpdatedAt} tarihli yayınlanmış liste fiyatlarına göre.`
          : undefined
      }
    >
      <div style={{ padding: '14px 16px 0' }}>
        <ErrorNote message={error} />
      </div>
      {loading && !overview && <Loading />}
      {ai && (
        <>
          <div className="metrics" style={{ padding: '0 16px 14px' }}>
            <Metric
              label="Tahmini harcama"
              value={money(ai.costMicroUsd)}
              hint={
                ai.unpricedCalls > 0
                  ? `${ai.unpricedCalls} çağrının fiyatı bilinmiyor`
                  : 'bu pencerede'
              }
              tone={ai.unpricedCalls > 0 ? 'bad' : undefined}
            />
            <Metric label="Modele giden" value={ai.analyzed} hint="para harcanan çağrı" />
            <Metric
              label="Önbellekten"
              value={ai.cacheHits}
              tone={ai.cacheHits > 0 ? 'good' : undefined}
              hint="hiç para harcamadı"
            />
            <Metric
              label="Başarısız iş"
              value={ai.jobs.failed}
              tone={ai.jobs.failed > 0 ? 'bad' : 'good'}
              hint={`${ai.jobs.queued} sırada · ${ai.jobs.running} çalışıyor`}
            />
          </div>

          {overview!.spendByModel.length === 0 ? (
            <Empty title="Bu pencerede hiç görsel modele gitmedi.">
              Ayarlardaki mod kapalıysa ya da bütün görseller önbellekten karşılandıysa bu
              normaldir.
            </Empty>
          ) : (
            <Table head={['Model', 'Çağrı', 'Girdi token', 'Çıktı token', 'Tutar']}>
              {overview!.spendByModel.map((row) => (
                <tr key={row.model}>
                  <td className="cell-title mono">{row.model}</td>
                  <td>{row.calls}</td>
                  <td className="muted">{compactNumber(row.inputTokens)}</td>
                  <td className="muted">{compactNumber(row.outputTokens)}</td>
                  <td className={row.costMicroUsd === null ? 'muted' : undefined}>
                    {row.costMicroUsd === null ? 'fiyat tanımsız' : money(row.costMicroUsd)}
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}
    </Card>
  );
}

/* -- tarama -------------------------------------------------------------- */

function ScrapingCard({ overview }: { overview: OperationsOverview | null }) {
  if (!overview) return null;
  const { runs, itemsFound, newItems, medianDurationMs } = overview.scraping;

  return (
    <Card title="Taramalar">
      <div className="metrics">
        <Metric
          label="Çalışma"
          value={runs.total}
          hint={`${runs.queued} sırada · ${runs.running} çalışıyor`}
        />
        <Metric
          label="Başarısız"
          value={runs.failed}
          tone={runs.failed > 0 ? 'bad' : 'good'}
          hint={`${runs.succeeded} başarılı`}
        />
        <Metric label="Toplanan öğe" value={itemsFound} hint={`${newItems} tanesi yeni`} />
        <Metric
          label="Tipik süre"
          value={duration(medianDurationMs)}
          hint="ortanca, ortalama değil"
        />
      </div>
    </Card>
  );
}

/* -- günlük seri --------------------------------------------------------- */

/**
 * Son iki haftanın günlük harcaması.
 *
 * Grafik kütüphanesi yok: on dört çubuk için bir bağımlılık eklemek, paketi de bakım
 * yükünü de gereksiz büyütürdü. Yükseklik en yüksek güne göre oranlanıyor.
 */
function DailyCard({
  points,
  loading,
  error,
}: {
  points: DailySpendPoint[];
  loading: boolean;
  error: string | null;
}) {
  const max = points.reduce((m, p) => Math.max(m, p.costMicroUsd), 0);
  const total = points.reduce((sum, p) => sum + p.costMicroUsd, 0);

  return (
    <Card
      title="Günlük harcama"
      footer={points.length > 0 ? `Son ${points.length} günün toplamı: ${money(total)}` : undefined}
    >
      <ErrorNote message={error} />
      {loading && points.length === 0 && <Loading />}
      {!loading && points.length === 0 && (
        <Empty title="Henüz harcama kaydı yok.">
          İlk görsel analizi çalıştığında burada gün gün görünecek.
        </Empty>
      )}
      {points.length > 0 && (
        <div className="bars">
          {points.map((p) => (
            <div className="bar" key={p.day}>
              <div
                className="bar-track"
                title={`${p.day}: ${money(p.costMicroUsd)} · ${p.analyzed} çağrı · ${p.cacheHits} önbellek`}
              >
                <div
                  className="bar-fill"
                  style={{
                    height: max > 0 ? `${Math.max(2, (p.costMicroUsd / max) * 100)}%` : '2%',
                  }}
                />
              </div>
              <span className="bar-label">{p.day.slice(5)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* -- hatalar ------------------------------------------------------------- */

function ErrorsCard({
  items,
  loading,
  error,
}: {
  items: OperationsError[];
  loading: boolean;
  error: string | null;
}) {
  const navigate = useNavigate();
  const [kind, setKind] = useState('');
  const [code, setCode] = useState('');
  const [q, setQ] = useState('');

  /**
   * Hata kodları veriden çıkarılıyor, sabit listeden değil.
   *
   * Kodlar motorun sınıflandırmasından geliyor ve yeni bir kod eklendiğinde panelin de
   * güncellenmesi gerekseydi, en yeni hata türü filtrede görünmeyen tek şey olurdu.
   * Sayılar da yanında: hangi hatanın baskın olduğu, listeyi okumadan görünüyor.
   */
  const kodlar = [...new Set(items.map((i) => i.code).filter((c): c is string => !!c))]
    .map((c) => ({ c, n: items.filter((i) => i.code === c).length }))
    .sort((a, b) => b.n - a.n);

  const arama = q.trim().toLocaleLowerCase('tr');
  const gorunen = items.filter((i) => {
    if (kind && i.kind !== kind) return false;
    if (code && i.code !== code) return false;
    if (!arama) return true;
    return [i.source, i.code, i.message]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('tr')
      .includes(arama);
  });

  const suzuldu = gorunen.length !== items.length;

  return (
    <Card
      title={`Hatalar${suzuldu ? ` (${gorunen.length}/${items.length})` : ` (${items.length})`}`}
      flush
    >
      {items.length > 0 && (
        <FilterBar>
          <input
            className="f-search"
            placeholder="Hesap, kod, hata metni…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">Tarama + görsel</option>
            <option value="run">Yalnız tarama</option>
            <option value="image">Yalnız görsel</option>
          </select>
          <select value={code} onChange={(e) => setCode(e.target.value)}>
            <option value="">Her kod</option>
            {kodlar.map((k) => (
              <option key={k.c} value={k.c}>
                {k.c} ({k.n})
              </option>
            ))}
          </select>
          {(kind || code || q) && (
            <button
              type="button"
              className="tiny ghost"
              onClick={() => {
                setKind('');
                setCode('');
                setQ('');
              }}
            >
              Temizle
            </button>
          )}
        </FilterBar>
      )}

      <div style={{ padding: '0 16px' }}>
        <ErrorNote message={error} />
      </div>
      {loading && items.length === 0 && <Loading />}
      {!loading && items.length === 0 && (
        <Empty title="Bu pencerede hiç hata yok.">Taramalar ve görsel analizi temiz.</Empty>
      )}
      {items.length > 0 && gorunen.length === 0 && (
        <Empty title="Filtreye uyan hata yok.">Filtreleri temizleyip tekrar bak.</Empty>
      )}
      {gorunen.length > 0 && (
        <Table head={['Nerede', 'Kod', 'Sebep', 'Deneme', 'Ne zaman', '']}>
          {gorunen.map((item) => (
            <tr key={`${item.kind}-${item.id}`}>
              <td className="cell-title">
                {item.kind === 'run' ? 'Tarama' : 'Görsel'} ·{' '}
                <span className="mono">{item.source}</span>
              </td>
              <td className="mono">{item.code ?? '—'}</td>
              {/* Sebep kırpılmıyor: teşhis eden şey cümlenin kendisi, kodu değil. */}
              <td className="muted cell-why">{item.message ?? '—'}</td>
              <td className="muted">
                {item.attempt}/{item.maxAttempts}
              </td>
              <td className="muted" title={stamp(item.at)}>
                {timeAgo(item.at)}
              </td>
              <td className="actions">
                {item.runId && (
                  <button
                    type="button"
                    className="ghost tiny"
                    onClick={() => navigate(`/runs/${item.runId}`)}
                  >
                    Çalışmayı aç
                  </button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Card>
  );
}

/* -- zamanlamalar -------------------------------------------------------- */

/** Cron yazmak zorunda kalmadan seçilebilecek yaygın aralıklar. */
const PRESETS = [
  { cron: '0 */6 * * *', label: '6 saatte bir' },
  { cron: '0 */12 * * *', label: '12 saatte bir' },
  { cron: '0 9 * * *', label: 'Her gün 09:00' },
  { cron: '0 9,21 * * *', label: 'Her gün 09:00 ve 21:00' },
  { cron: '0 9 * * 1', label: 'Her pazartesi 09:00' },
];

function SchedulesCard({
  items,
  loading,
  error,
  reload,
}: {
  items: Schedule[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function act(run: () => Promise<unknown>) {
    setBusy(true);
    setActionError(null);
    try {
      await run();
      reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'İşlem başarısız.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={`Otomatik taramalar (${items.length})`}
      flush
      actions={
        <button type="button" onClick={() => setCreating(true)}>
          Zamanlama ekle
        </button>
      }
      footer="Zamanlanmış tarama bir operatöre ait, bir API anahtarına değil: anahtar iptal edilse de zamanlama çalışmaya devam eder."
    >
      <div style={{ padding: '14px 16px 0' }}>
        <ErrorNote message={error ?? actionError} />
      </div>
      {loading && items.length === 0 && <Loading />}
      {!loading && items.length === 0 && (
        <Empty title="Hiç otomatik tarama yok.">
          Her tarama şu an elle başlatılıyor. Zamanlama eklersen platform kendi başlatır.
        </Empty>
      )}
      {items.length > 0 && (
        <Table head={['Ad', 'Actor', 'Ne zaman', 'Son çalışma', 'Sıradaki']}>
          {items.map((s) => (
            <tr key={s.id}>
              <td className="cell-title">{s.name}</td>
              <td className="mono muted">{s.actorSlug}</td>
              <td className="muted">
                {PRESETS.find((p) => p.cron === s.cron)?.label ?? s.cron}
                <span className="small muted"> · {s.timezone}</span>
              </td>
              <td className="muted" title={stamp(s.lastRunAt)}>
                {timeAgo(s.lastRunAt)}
              </td>
              <td className="muted" title={stamp(s.nextRunAt)}>
                {s.isEnabled ? stamp(s.nextRunAt) : 'kapalı'}
              </td>
              <td className="actions">
                <button
                  type="button"
                  className="tiny"
                  disabled={busy}
                  onClick={() => void act(() => api.post(`/v1/schedules/${s.id}/run`))}
                >
                  Şimdi çalıştır
                </button>
                <Toggle
                  on={s.isEnabled}
                  label={`${s.name} zamanlaması`}
                  disabled={busy}
                  onChange={(next) =>
                    void act(() => api.patch(`/v1/schedules/${s.id}`, { isEnabled: next }))
                  }
                />
                <ConfirmButton
                  label="Sil"
                  confirmLabel="Emin misin?"
                  onConfirm={() => void act(() => api.del(`/v1/schedules/${s.id}`))}
                />
              </td>
            </tr>
          ))}
        </Table>
      )}

      {creating && (
        <CreateSchedule
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            reload();
          }}
        />
      )}
    </Card>
  );
}

function CreateSchedule({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const actors = useLoad(() => api.get<Page<ActorSummary>>('/v1/actors?limit=100'));
  const [name, setName] = useState('');
  const [actorSlug, setActorSlug] = useState('');
  const [cron, setCron] = useState(PRESETS[0]!.cron);
  const [input, setInput] = useState('{\n  \n}');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    let parsed: unknown;
    try {
      parsed = JSON.parse(input);
    } catch {
      // Girdi burada ayrıştırılıyor ama DOĞRULANMIYOR: hangi girdinin geçerli olduğuna
      // actor'ın kendi şeması karar veriyor ve ilk çalışmada söylüyor. Panelde ikinci
      // bir tanım tutmak, iki farklı "geçerli girdi" anlamı üretirdi.
      setError('Girdi geçerli JSON değil.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/v1/schedules', { name, actorSlug, cron, input: parsed });
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Zamanlama eklenemedi.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Zamanlama ekle"
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel="Ekle"
      busy={busy}
      error={error}
    >
      <Field label="Ad" hint="Panelde bu zamanlamayı tanıyacağın isim.">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Zorlu PSM" />
      </Field>
      <Field label="Actor">
        <select value={actorSlug} onChange={(e) => setActorSlug(e.target.value)}>
          <option value="">seç…</option>
          {(actors.data?.items ?? []).map((a) => (
            <option key={a.slug} value={a.slug}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Ne zaman" hint="Saat dilimi Europe/Istanbul.">
        <select value={cron} onChange={(e) => setCron(e.target.value)}>
          {PRESETS.map((p) => (
            <option key={p.cron} value={p.cron}>
              {p.label}
            </option>
          ))}
        </select>
      </Field>
      <Field
        label="Girdi"
        hint="Actor'a gönderilecek JSON. Geçerliliğine actor'ın kendi şeması karar verir; yanlışsa ilk çalışmada söyler."
      >
        <textarea rows={6} value={input} onChange={(e) => setInput(e.target.value)} />
      </Field>
    </Modal>
  );
}
